import React, { useState, useEffect } from 'react';
import { getAuthToken, getAuthHeaders, authFetch } from '../../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FullStrategyView = ({ projectId, user, onBack }) => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [humanizing, setHumanizing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [activeEmailIndex, setActiveEmailIndex] = useState(0);
  const [activeAdIndex, setActiveAdIndex] = useState(0);
  const [activeSocialIndex, setActiveSocialIndex] = useState(0);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showHumanizeModal, setShowHumanizeModal] = useState(false);
  const [humanizeLevel, setHumanizeLevel] = useState('natural');

  useEffect(() => {
    if (projectId) {
      fetchProject();
    }
  }, [projectId]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setProject(data);
      } else {
        setError('Impossible de charger le projet');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const generateContent = async () => {
    try {
      setGenerating(true);
      setError('');
      setSuccessMessage('Génération de vos contenus marketing en cours... Cela peut prendre 30 à 60 secondes.');
      
      const response = await fetch(`${API_URL}/api/projects/generate-content/${projectId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProject(data);
        setSuccessMessage('Tous vos contenus marketing ont été générés avec succès !');
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Erreur lors de la génération');
        setSuccessMessage('');
      }
    } catch (err) {
      setError('Erreur de connexion');
      setSuccessMessage('');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage('Copié dans le presse-papier !');
    setTimeout(() => setSuccessMessage(''), 2000);
  };

  // Export functions
  const exportToPDF = async () => {
    try {
      setExporting(true);
      setError('');
      const token = getAuthToken();
      
      const response = await fetch(`${API_URL}/api/projects/export/${projectId}/pdf`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `strategie_${project?.name || 'projet'}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccessMessage('PDF téléchargé avec succès !');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Erreur lors de l\'export PDF');
      }
    } catch (err) {
      setError('Erreur de connexion lors de l\'export');
    } finally {
      setExporting(false);
      setShowExportMenu(false);
    }
  };

  const exportToWord = async () => {
    try {
      setExporting(true);
      setError('');
      const token = getAuthToken();
      
      const response = await fetch(`${API_URL}/api/projects/export/${projectId}/word`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `strategie_${project?.name || 'projet'}_${new Date().toISOString().split('T')[0]}.docx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccessMessage('Document Word téléchargé avec succès !');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Erreur lors de l\'export Word');
      }
    } catch (err) {
      setError('Erreur de connexion lors de l\'export');
    } finally {
      setExporting(false);
      setShowExportMenu(false);
    }
  };

  // Humanize function
  const humanizeContent = async () => {
    try {
      setHumanizing(true);
      setError('');
      const token = getAuthToken();
      
      const response = await fetch(`${API_URL}/api/projects/humanize/${projectId}?level=${humanizeLevel}&style=professional`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(data.message || 'Contenus humanisés avec succès !');
        setTimeout(() => setSuccessMessage(''), 3000);
        // Reload project to show humanized content
        fetchProject();
        setShowHumanizeModal(false);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Erreur lors de l\'humanisation');
      }
    } catch (err) {
      setError('Erreur de connexion lors de l\'humanisation');
    } finally {
      setHumanizing(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: '📊' },
    { id: 'avatar', label: 'Avatar Client', icon: '👤' },
    { id: 'positioning', label: 'Positionnement', icon: '🎯' },
    { id: 'offer', label: 'Offre', icon: '💡' },
    { id: 'sales-page', label: 'Page de Vente', icon: '📄' },
    { id: 'emails', label: 'Emails', icon: '✉️' },
    { id: 'facebook', label: 'Facebook Ads', icon: '📱' },
    { id: 'google', label: 'Google Ads', icon: '🔍' },
    { id: 'social', label: 'Posts Social', icon: '📲' },
    { id: 'calendar', label: 'Calendrier', icon: '📅' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement du projet...</p>
        </div>
      </div>
    );
  }

  if (error && !project) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button onClick={onBack} className="px-4 py-2 bg-gray-800 text-white rounded-lg">
            Retour
          </button>
        </div>
      </div>
    );
  }

  const hasContent = project?.marketing_content?.sales_page || 
                     project?.marketing_content?.email_sequence?.length > 0;

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="full-strategy-view">
      {/* Header */}
      <header className="border-b border-gray-800 bg-[#0a0a0f]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button 
              onClick={onBack}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white"
              data-testid="strategy-back-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
              </svg>
            </button>
            <div>
              <h1 className="text-lg font-semibold text-white">{project?.name || 'Stratégie Marketing'}</h1>
              <p className="text-xs text-gray-500">
                {project?.status === 'complete' ? '✅ Stratégie complète' : '⏳ En cours'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-3">
            {!hasContent && (
              <button
                onClick={generateContent}
                disabled={generating}
                className="px-3 sm:px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg font-medium text-xs sm:text-sm transition-all flex items-center space-x-1 sm:space-x-2 disabled:opacity-50"
                data-testid="generate-content-btn"
              >
                {generating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span className="hidden sm:inline">Génération...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                    </svg>
                    <span className="hidden sm:inline">Générer les contenus</span>
                    <span className="sm:hidden">Générer</span>
                  </>
                )}
              </button>
            )}
            
            {/* Humanize Button */}
            {hasContent && (
              <button
                onClick={() => setShowHumanizeModal(true)}
                disabled={humanizing}
                className="px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-xs sm:text-sm transition-all flex items-center space-x-1 sm:space-x-2 disabled:opacity-50"
                data-testid="humanize-btn"
              >
                {humanizing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span className="hidden sm:inline">Humanisation...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                    <span className="hidden sm:inline">Humaniser</span>
                  </>
                )}
              </button>
            )}
            
            {/* Export Dropdown */}
            <div className="relative">
              <button 
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={exporting}
                className="px-3 sm:px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium text-xs sm:text-sm transition-all flex items-center space-x-1 sm:space-x-2 disabled:opacity-50"
                data-testid="export-dropdown-btn"
              >
                {exporting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span className="hidden sm:inline">Export...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                    </svg>
                    <span className="hidden sm:inline">Exporter</span>
                    <svg className="w-4 h-4 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </>
                )}
              </button>
              
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50">
                  <button
                    onClick={exportToPDF}
                    className="w-full px-4 py-3 text-left text-white hover:bg-gray-800 flex items-center space-x-3 rounded-t-lg transition-colors"
                    data-testid="export-pdf-btn"
                  >
                    <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
                    </svg>
                    <div>
                      <p className="font-medium">Export PDF</p>
                      <p className="text-xs text-gray-400">Document formaté</p>
                    </div>
                  </button>
                  <button
                    onClick={exportToWord}
                    className="w-full px-4 py-3 text-left text-white hover:bg-gray-800 flex items-center space-x-3 rounded-b-lg transition-colors border-t border-gray-700"
                    data-testid="export-word-btn"
                  >
                    <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
                    </svg>
                    <div>
                      <p className="font-medium">Export Word</p>
                      <p className="text-xs text-gray-400">Document modifiable</p>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row">
        {/* Mobile Tab Navigation */}
        <div className="lg:hidden border-b border-gray-800 bg-[#0a0a0f] p-2 overflow-x-auto">
          <div className="flex space-x-2 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                }`}
                data-testid={`mobile-tab-${tab.id}`}
              >
                <span>{tab.icon}</span>
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Desktop Sidebar Navigation */}
        <nav className="w-64 border-r border-gray-800 min-h-[calc(100vh-65px)] bg-[#0a0a0f] p-4 hidden lg:block flex-shrink-0">
          <div className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-all ${
                  activeTab === tab.id
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                }`}
              >
                <span className="text-lg">{tab.icon}</span>
                <span className="font-medium text-sm">{tab.label}</span>
              </button>
            ))}
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-y-auto">
          {/* Mobile Tab Selector */}
          <div className="lg:hidden mb-6 overflow-x-auto">
            <div className="flex space-x-2 pb-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-800 text-gray-400'
                  }`}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <OverviewTab project={project} hasContent={hasContent} />
          )}
          
          {activeTab === 'avatar' && project?.client_avatar && (
            <AvatarTab avatar={project.client_avatar} copyToClipboard={copyToClipboard} />
          )}
          
          {activeTab === 'positioning' && project?.positioning && (
            <PositioningTab positioning={project.positioning} copyToClipboard={copyToClipboard} />
          )}
          
          {activeTab === 'offer' && project?.commercial_offer && (
            <OfferTab offer={project.commercial_offer} copyToClipboard={copyToClipboard} />
          )}
          
          {activeTab === 'sales-page' && project?.marketing_content?.sales_page && (
            <SalesPageTab salesPage={project.marketing_content.sales_page} copyToClipboard={copyToClipboard} />
          )}
          
          {activeTab === 'emails' && project?.marketing_content?.email_sequence && (
            <EmailsTab 
              emails={project.marketing_content.email_sequence} 
              activeIndex={activeEmailIndex}
              setActiveIndex={setActiveEmailIndex}
              copyToClipboard={copyToClipboard} 
            />
          )}
          
          {activeTab === 'facebook' && project?.marketing_content?.facebook_ads && (
            <FacebookAdsTab 
              ads={project.marketing_content.facebook_ads}
              activeIndex={activeAdIndex}
              setActiveIndex={setActiveAdIndex}
              copyToClipboard={copyToClipboard}
            />
          )}
          
          {activeTab === 'google' && project?.marketing_content?.google_ads && (
            <GoogleAdsTab 
              ads={project.marketing_content.google_ads}
              copyToClipboard={copyToClipboard}
            />
          )}
          
          {activeTab === 'social' && project?.marketing_content?.social_posts && (
            <SocialPostsTab 
              posts={project.marketing_content.social_posts}
              activeIndex={activeSocialIndex}
              setActiveIndex={setActiveSocialIndex}
              copyToClipboard={copyToClipboard}
            />
          )}
          
          {activeTab === 'calendar' && project?.marketing_content?.editorial_calendar && (
            <CalendarTab calendar={project.marketing_content.editorial_calendar} />
          )}

          {/* Empty State for missing content */}
          {!hasContent && activeTab !== 'overview' && activeTab !== 'avatar' && 
           activeTab !== 'positioning' && activeTab !== 'offer' && (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-2xl bg-gray-800/50 flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Contenus non générés</h3>
              <p className="text-gray-400 mb-6">Cliquez sur "Générer les contenus" pour créer vos contenus marketing.</p>
              <button
                onClick={generateContent}
                disabled={generating}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium"
              >
                Générer maintenant
              </button>
            </div>
          )}
        </main>
      </div>

      {/* Success Message Toast */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center space-x-2 z-50 animate-fade-in-up" data-testid="success-toast">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
          </svg>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Error Message Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center space-x-2 z-50" data-testid="error-toast">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
          </svg>
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-2 text-white/80 hover:text-white">×</button>
        </div>
      )}

      {/* Humanize Modal */}
      {showHumanizeModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="humanize-modal">
          <div className="bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4 border border-gray-700">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">Humaniser les contenus</h3>
              <button onClick={() => setShowHumanizeModal(false)} className="text-gray-400 hover:text-white">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <p className="text-gray-400 mb-6 text-sm">
              L'humanisation rend vos contenus plus naturels et moins "IA". Choisissez le niveau d'humanisation souhaité.
            </p>
            
            <div className="space-y-3 mb-6">
              {[
                { value: 'standard', label: 'Standard', desc: 'Corrections basiques, fluidité améliorée' },
                { value: 'natural', label: 'Naturel', desc: 'Expressions humaines, ton conversationnel' },
                { value: 'premium', label: 'Premium', desc: 'Réécriture créative, variations stylistiques' },
                { value: 'expert', label: 'Expert', desc: 'Qualité indiscernable d\'un rédacteur humain' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setHumanizeLevel(option.value)}
                  className={`w-full p-4 rounded-xl text-left transition-all ${
                    humanizeLevel === option.value 
                      ? 'bg-purple-500/20 border-2 border-purple-500' 
                      : 'bg-gray-800 border-2 border-transparent hover:border-gray-600'
                  }`}
                  data-testid={`humanize-level-${option.value}`}
                >
                  <p className={`font-medium ${humanizeLevel === option.value ? 'text-purple-400' : 'text-white'}`}>
                    {option.label}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">{option.desc}</p>
                </button>
              ))}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowHumanizeModal(false)}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={humanizeContent}
                disabled={humanizing}
                className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                data-testid="confirm-humanize-btn"
              >
                {humanizing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Humanisation...</span>
                  </>
                ) : (
                  <span>Humaniser</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close export menu */}
      {showExportMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowExportMenu(false)}
        ></div>
      )}
    </div>
  );
};

// Sub-components for each tab

const OverviewTab = ({ project, hasContent }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[
        { label: 'Avatar Client', done: !!project?.client_avatar, icon: '👤' },
        { label: 'Positionnement', done: !!project?.positioning, icon: '🎯' },
        { label: 'Offre', done: !!project?.commercial_offer, icon: '💡' },
        { label: 'Page de Vente', done: !!project?.marketing_content?.sales_page, icon: '📄' },
        { label: 'Emails (5)', done: project?.marketing_content?.email_sequence?.length > 0, icon: '✉️' },
        { label: 'Facebook Ads', done: project?.marketing_content?.facebook_ads?.length > 0, icon: '📱' },
        { label: 'Google Ads', done: project?.marketing_content?.google_ads?.length > 0, icon: '🔍' },
        { label: 'Posts Social', done: project?.marketing_content?.social_posts?.length > 0, icon: '📲' },
      ].map((item, i) => (
        <div key={i} className={`p-4 rounded-xl border ${item.done ? 'bg-green-500/10 border-green-500/30' : 'bg-gray-900/30 border-gray-800'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl">{item.icon}</span>
            {item.done ? (
              <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
              </svg>
            ) : (
              <div className="w-5 h-5 rounded-full border-2 border-gray-600"></div>
            )}
          </div>
          <p className={`text-sm font-medium ${item.done ? 'text-green-400' : 'text-gray-500'}`}>{item.label}</p>
        </div>
      ))}
    </div>

    {project?.positioning && (
      <div className="p-6 bg-gray-900/50 rounded-2xl border border-gray-800">
        <h3 className="text-lg font-semibold text-white mb-4">Résumé Stratégique</h3>
        <div className="space-y-4">
          <div>
            <span className="text-xs text-gray-500 uppercase">Tagline</span>
            <p className="text-xl font-bold text-green-400">"{project.positioning.tagline}"</p>
          </div>
          <div>
            <span className="text-xs text-gray-500 uppercase">Proposition de valeur</span>
            <p className="text-gray-300">{project.positioning.unique_value_proposition}</p>
          </div>
        </div>
      </div>
    )}
  </div>
);

const AvatarTab = ({ avatar, copyToClipboard }) => (
  <div className="max-w-3xl space-y-6">
    <div className="flex items-start space-x-4 p-6 bg-gray-900/50 rounded-2xl border border-gray-800">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-3xl font-bold flex-shrink-0">
        {avatar.name?.charAt(0)}
      </div>
      <div>
        <h2 className="text-2xl font-bold text-white">{avatar.name}</h2>
        <p className="text-gray-400">{avatar.age_range} • {avatar.profession}</p>
        <p className="text-gray-500 mt-2">{avatar.situation}</p>
      </div>
    </div>

    <div className="grid md:grid-cols-2 gap-6">
      <ContentCard title="😤 Frustrations" items={avatar.frustrations} color="red" copyToClipboard={copyToClipboard} />
      <ContentCard title="💚 Désirs" items={avatar.desires} color="green" copyToClipboard={copyToClipboard} />
      <ContentCard title="🤔 Objections" items={avatar.objections} color="yellow" copyToClipboard={copyToClipboard} />
      <ContentCard title="⚡ Déclencheurs" items={avatar.triggers} color="blue" copyToClipboard={copyToClipboard} />
    </div>

    <div className="p-6 bg-gray-900/50 rounded-2xl border border-gray-800 border-l-4 border-l-purple-500">
      <p className="text-gray-400 text-sm mb-2">Citation typique</p>
      <p className="text-xl text-white italic">"{avatar.quote}"</p>
    </div>
  </div>
);

const ContentCard = ({ title, items, color, copyToClipboard }) => {
  const colors = {
    red: 'text-red-400',
    green: 'text-green-400',
    yellow: 'text-yellow-400',
    blue: 'text-blue-400'
  };
  
  return (
    <div className="p-5 bg-gray-900/50 rounded-xl border border-gray-800">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-white">{title}</h3>
        <button 
          onClick={() => copyToClipboard(items.join('\n'))}
          className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-500 hover:text-white transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
        </button>
      </div>
      <ul className="space-y-2">
        {items?.map((item, i) => (
          <li key={i} className="flex items-start space-x-2 text-sm">
            <span className={`mt-1 ${colors[color]}`}>•</span>
            <span className="text-gray-300">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PositioningTab = ({ positioning, copyToClipboard }) => (
  <div className="max-w-3xl space-y-6">
    <div className="p-6 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-2xl border border-blue-500/20">
      <h2 className="text-3xl font-bold text-white mb-2">"{positioning.tagline}"</h2>
      <p className="text-gray-400">{positioning.unique_value_proposition}</p>
    </div>

    <div className="p-6 bg-gray-900/50 rounded-2xl border border-gray-800">
      <h3 className="font-semibold text-white mb-3">Déclaration de positionnement</h3>
      <p className="text-gray-300 leading-relaxed">{positioning.positioning_statement}</p>
    </div>

    <div className="grid md:grid-cols-2 gap-6">
      <ContentCard title="🎯 Différenciateurs" items={positioning.key_differentiators} color="blue" copyToClipboard={copyToClipboard} />
      <ContentCard title="✨ Personnalité de marque" items={positioning.brand_personality} color="green" copyToClipboard={copyToClipboard} />
    </div>

    <div className="p-6 bg-gray-900/50 rounded-2xl border border-gray-800">
      <h3 className="font-semibold text-white mb-3">Ton de communication</h3>
      <p className="text-gray-300">{positioning.tone_of_voice}</p>
    </div>
  </div>
);

const OfferTab = ({ offer, copyToClipboard }) => (
  <div className="max-w-3xl space-y-6">
    <div className="p-6 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-2xl border border-orange-500/20">
      <h2 className="text-2xl font-bold text-white mb-2">{offer.offer_name}</h2>
      <p className="text-lg text-gray-300">{offer.promise}</p>
    </div>

    <div className="space-y-4">
      <h3 className="font-semibold text-white">Arguments de vente</h3>
      {offer.sales_arguments?.map((arg, i) => (
        <div key={i} className="p-4 bg-gray-900/50 rounded-xl border border-gray-800 flex items-start space-x-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-bold flex-shrink-0">
            {i + 1}
          </div>
          <div>
            <h4 className="font-medium text-white">{arg.title}</h4>
            <p className="text-sm text-gray-400 mt-1">{arg.description}</p>
          </div>
        </div>
      ))}
    </div>

    <div className="grid md:grid-cols-2 gap-6">
      <ContentCard title="✅ Caractéristiques" items={offer.features} color="blue" copyToClipboard={copyToClipboard} />
      <ContentCard title="🎁 Bonus" items={offer.bonuses} color="green" copyToClipboard={copyToClipboard} />
    </div>

    <div className="p-6 bg-green-500/10 rounded-2xl border border-green-500/20">
      <h3 className="font-semibold text-green-400 mb-2">🛡️ Garantie</h3>
      <p className="text-gray-300">{offer.guarantee}</p>
    </div>
  </div>
);

const SalesPageTab = ({ salesPage, copyToClipboard }) => (
  <div className="max-w-3xl space-y-6">
    <div className="p-6 bg-gray-900/50 rounded-2xl border border-gray-800">
      <h2 className="text-3xl font-bold text-white mb-4">{salesPage.headline}</h2>
      <p className="text-xl text-gray-400">{salesPage.subheadline}</p>
    </div>

    <div className="p-6 bg-gray-900/50 rounded-2xl border border-gray-800">
      <h3 className="font-semibold text-white mb-3">Accroche</h3>
      <p className="text-gray-300">{salesPage.hook_paragraph}</p>
    </div>

    {salesPage.problem_section && (
      <div className="p-6 bg-red-500/5 rounded-2xl border border-red-500/20">
        <h3 className="font-semibold text-red-400 mb-4">{salesPage.problem_section.title}</h3>
        <ul className="space-y-2">
          {salesPage.problem_section.problems?.map((p, i) => (
            <li key={i} className="flex items-start space-x-2 text-gray-300">
              <span className="text-red-400">✗</span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>
    )}

    {salesPage.solution_section && (
      <div className="p-6 bg-green-500/5 rounded-2xl border border-green-500/20">
        <h3 className="font-semibold text-green-400 mb-4">{salesPage.solution_section.title}</h3>
        <p className="text-gray-300 mb-4">{salesPage.solution_section.intro}</p>
        <div className="space-y-3">
          {salesPage.solution_section.benefits?.map((b, i) => (
            <div key={i} className="flex items-start space-x-3">
              <span className="text-green-400">✓</span>
              <div>
                <span className="font-medium text-white">{b.title}</span>
                <span className="text-gray-400"> - {b.description}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl border border-green-500/30 text-center">
      <button className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold text-lg">
        {salesPage.cta_primary}
      </button>
      <p className="text-gray-400 text-sm mt-2">{salesPage.cta_secondary}</p>
    </div>

    {salesPage.faq && (
      <div className="p-6 bg-gray-900/50 rounded-2xl border border-gray-800">
        <h3 className="font-semibold text-white mb-4">FAQ</h3>
        <div className="space-y-4">
          {salesPage.faq?.map((item, i) => (
            <div key={i}>
              <h4 className="font-medium text-white">{item.question}</h4>
              <p className="text-gray-400 text-sm mt-1">{item.answer}</p>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const EmailsTab = ({ emails, activeIndex, setActiveIndex, copyToClipboard }) => (
  <div className="space-y-6">
    <div className="flex space-x-2 overflow-x-auto pb-2">
      {emails?.map((email, i) => (
        <button
          key={i}
          onClick={() => setActiveIndex(i)}
          className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-all ${
            activeIndex === i
              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
              : 'bg-gray-800 text-gray-400 hover:text-white'
          }`}
        >
          Email {i + 1} - {email.type}
        </button>
      ))}
    </div>

    {emails?.[activeIndex] && (
      <div className="max-w-3xl space-y-4">
        <div className="p-6 bg-gray-900/50 rounded-2xl border border-gray-800">
          <div className="flex justify-between items-start mb-4">
            <div>
              <span className="text-xs text-gray-500 uppercase">Envoyer à</span>
              <p className="text-sm text-gray-400">{emails[activeIndex].send_day}</p>
            </div>
            <button 
              onClick={() => copyToClipboard(emails[activeIndex].body)}
              className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-400 hover:text-white transition-colors"
            >
              Copier
            </button>
          </div>

          <div className="border-b border-gray-700 pb-4 mb-4">
            <span className="text-xs text-gray-500 uppercase">Objet</span>
            <p className="text-lg font-semibold text-white">{emails[activeIndex].subject}</p>
            <p className="text-sm text-gray-500">{emails[activeIndex].preview_text}</p>
          </div>

          <div className="prose prose-invert max-w-none">
            <div className="text-gray-300 whitespace-pre-wrap">{emails[activeIndex].body}</div>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-700">
            <button className="px-6 py-2 bg-purple-500/20 text-purple-400 rounded-lg font-medium">
              {emails[activeIndex].cta_text}
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);

const FacebookAdsTab = ({ ads, activeIndex, setActiveIndex, copyToClipboard }) => (
  <div className="space-y-6">
    <div className="flex space-x-2 overflow-x-auto pb-2">
      {ads?.map((ad, i) => (
        <button
          key={i}
          onClick={() => setActiveIndex(i)}
          className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-all ${
            activeIndex === i
              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              : 'bg-gray-800 text-gray-400 hover:text-white'
          }`}
        >
          Ad {i + 1} - {ad.angle?.replace('_', ' ')}
        </button>
      ))}
    </div>

    {ads?.[activeIndex] && (
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-xl overflow-hidden shadow-xl">
          {/* Facebook Ad Preview */}
          <div className="p-4 border-b flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full"></div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Votre Page</p>
              <p className="text-xs text-gray-500">Sponsorisé</p>
            </div>
          </div>
          <div className="p-4">
            <p className="text-gray-900 text-sm whitespace-pre-wrap">{ads[activeIndex].hook}</p>
            <p className="text-gray-800 text-sm mt-2 whitespace-pre-wrap">{ads[activeIndex].primary_text}</p>
          </div>
          <div className="bg-gray-100 p-4">
            <p className="text-xs text-gray-500 uppercase">votresite.com</p>
            <p className="font-bold text-gray-900">{ads[activeIndex].headline}</p>
            <p className="text-sm text-gray-600">{ads[activeIndex].description}</p>
          </div>
          <div className="p-4 border-t">
            <button className="w-full py-2 bg-blue-600 text-white rounded-lg font-semibold text-sm">
              {ads[activeIndex].cta}
            </button>
          </div>
        </div>

        <button 
          onClick={() => copyToClipboard(`${ads[activeIndex].hook}\n\n${ads[activeIndex].primary_text}`)}
          className="mt-4 w-full py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm"
        >
          Copier le texte
        </button>
      </div>
    )}
  </div>
);

const GoogleAdsTab = ({ ads, copyToClipboard }) => (
  <div className="grid gap-6 max-w-2xl">
    {ads?.map((ad, i) => (
      <div key={i} className="p-6 bg-gray-900/50 rounded-2xl border border-gray-800">
        <div className="flex justify-between items-start mb-4">
          <h3 className="font-semibold text-white">Annonce {i + 1}</h3>
          <button 
            onClick={() => copyToClipboard(ad.headlines.join(' | ') + '\n' + ad.descriptions.join(' '))}
            className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded text-xs text-gray-400"
          >
            Copier
          </button>
        </div>

        {/* Google Ad Preview */}
        <div className="bg-white rounded-lg p-4">
          <p className="text-xs text-gray-500">Ad • votresite.com{ad.target_url_suffix}</p>
          <p className="text-blue-600 font-medium">
            {ad.headlines?.slice(0, 3).join(' | ')}
          </p>
          <p className="text-sm text-gray-700 mt-1">
            {ad.descriptions?.join(' ')}
          </p>
        </div>

        <div className="mt-4">
          <p className="text-xs text-gray-500 mb-2">Mots-clés suggérés</p>
          <div className="flex flex-wrap gap-2">
            {ad.keywords?.map((kw, j) => (
              <span key={j} className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
                {kw}
              </span>
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
);

const SocialPostsTab = ({ posts, activeIndex, setActiveIndex, copyToClipboard }) => (
  <div className="space-y-6">
    <div className="flex flex-wrap gap-2">
      {posts?.map((post, i) => (
        <button
          key={i}
          onClick={() => setActiveIndex(i)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            activeIndex === i
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : 'bg-gray-800 text-gray-400 hover:text-white'
          }`}
        >
          #{i + 1} {post.type}
        </button>
      ))}
    </div>

    {posts?.[activeIndex] && (
      <div className="max-w-xl">
        <div className="p-6 bg-gray-900/50 rounded-2xl border border-gray-800">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center space-x-2">
              <span className="text-xs px-2 py-1 bg-gray-800 rounded text-gray-400">
                {posts[activeIndex].platform}
              </span>
              <span className="text-xs px-2 py-1 bg-blue-500/20 rounded text-blue-400">
                {posts[activeIndex].type}
              </span>
            </div>
            <button 
              onClick={() => copyToClipboard(posts[activeIndex].content)}
              className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded text-xs text-gray-400"
            >
              Copier
            </button>
          </div>

          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300 whitespace-pre-wrap">{posts[activeIndex].content}</p>
          </div>

          {posts[activeIndex].hashtags && (
            <div className="mt-4 flex flex-wrap gap-2">
              {posts[activeIndex].hashtags.map((tag, i) => (
                <span key={i} className="text-blue-400 text-sm">{tag}</span>
              ))}
            </div>
          )}

          {posts[activeIndex].cta && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-sm text-gray-500">CTA: {posts[activeIndex].cta}</p>
            </div>
          )}

          <div className="mt-4 text-xs text-gray-500">
            📅 Meilleur moment: {posts[activeIndex].best_time}
          </div>
        </div>
      </div>
    )}
  </div>
);

const CalendarTab = ({ calendar }) => (
  <div className="space-y-4">
    <div className="grid gap-2">
      {calendar?.slice(0, 14).map((day, i) => (
        <div key={i} className="p-4 bg-gray-900/50 rounded-xl border border-gray-800 flex items-center space-x-4">
          <div className="text-center min-w-[60px]">
            <p className="text-xs text-gray-500">{day.day_name}</p>
            <p className="text-lg font-bold text-white">J{day.day}</p>
          </div>
          <div className="flex-1 flex flex-wrap gap-2">
            {day.tasks?.map((task, j) => (
              <span 
                key={j}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  task.type === 'social' ? 'bg-blue-500/20 text-blue-400' :
                  task.type === 'email' ? 'bg-purple-500/20 text-purple-400' :
                  task.type === 'ad' ? 'bg-orange-500/20 text-orange-400' :
                  'bg-gray-700 text-gray-400'
                }`}
              >
                {task.platform || task.type}: {task.title}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
    {calendar?.length > 14 && (
      <p className="text-center text-gray-500 text-sm">
        + {calendar.length - 14} jours supplémentaires
      </p>
    )}
  </div>
);

export default FullStrategyView;
